// The HUD aim and targeting widgets: the client half (`GraphicUpdate`) of the WormHUDMan widgets
// whose presentation members the sim keeps (crates/sim/src/present/hud/hud_widgets.rs, exported
// by the frame view's `widgets` tail; docs/platform/RenderFidelity.md "Gadget aim helpers"):
// - WormHUDAimReticle (the crosshair) 0xcf7780: HU_Reticle_01 in PPL_GUIGAMEFG at
//   WorldToHUD(m_vPosition) + m_vOffset, rotated, the current team's flat colour as TintColor,
//   its "Go" clip replayed from 0 at every activation;
// - WormHUDPowerBar (the charge) 0xc91040: HU_PowerBar_01 in PPL_GUIGAMEBG (behind the worms) at
//   WorldToHUD(m_vPosition), rotated, "Go" at time 0 with weight 1 - power (the gradient's U
//   offset 0.4 × (1 - power));
// - TargetCursor 0xc388f0: k_Target = HU_TargetSquare_01 (team colour when the position is valid,
//   else white, "Go" on a phase set or a failed placement); k_Directional = the
//   HU_AirStrikeArrow_01 quad (team colour, or grey at 0.75 alpha when invalid);
// - WormHUDSelect 0xafabf0: HU_WormSelect_01, looping "Go", team colour, 175 above the worm;
// - WormHUDFuseTimer 0xbcfe20 / DrawPanel 0xbcf490: "%i" of the seconds + 1 in WMD-SerifBold 20 on
//   the name panel box, for 0 ≤ time ≤ 5, at WorldToHUD + (-25, 55);
// - WormHUDGadgetIcon 0xb1d2e0 / DrawPanel 0xb1cbc0: the chained equip's icon on the name panel
//   box at WorldToHUD + (50, 82), scale 0.6.
// - WormHUDActivity 0xd08d10 / DrawTracker 0xd08800: HU_SideArrow_01 and the distance in
//   WMD-SerifBold 18 on the screen's ellipse towards each off-screen activity object.
// The reticle and the power bar hide while `WormMan::IsWormHiddingFromLocalPlayer(-1)` 0xc99ae0.
import type { Renderer } from '../render/renderer.ts';
import type { ActivityObject, FrameView, Widgets } from '../sim/view.ts';
import { DESIGN_H, DESIGN_W } from '../ui/ip2/ip2.ts';

type Rgb = [number, number, number];
type Rgba4 = [number, number, number, number];
/** A design-space affine of a sprite pixel (px, py; y down): (m0 px + m2 py + m4, m1 px + m3 py + m5), y up. */
export type Affine = [number, number, number, number, number, number];
/** The IP2 painter calls the 2-D widgets need (hud.ts provides them). */
export type WidgetPainter = {
  /** HUD design coordinates (origin at the centre, y up) to canvas pixels. */
  toCanvas(x: number, y: number): { x: number; y: number };
  /** Canvas pixels per design unit. */
  s: number;
  /** The text's width in WMD-SerifBold 20, design units. */
  textWidth(text: string): number;
  /** A UI texture (reslist index or file name) stretched over the canvas rectangle, tinted (RGBA 0..1). */
  texture(ref: number | string, rect: [number, number, number, number], colour: Rgba4): void;
  /** WMD-SerifBold 20 centred in the box with the label's drop shadow. */
  text(text: string, box: [number, number, number, number], colour: Rgba4): void;
  /** The UI texture of an equip's icon (`EquipGetUIIconID`), null when unknown. */
  equipIcon(equip: number): number | string | null;
  /** A UI texture's size in design units (null until loaded). */
  size(ref: number | string): { w: number; h: number } | null;
  /** A UI texture's `cRM_Texture` m_Width/m_Height (the kept sprite) and crop (m_CropOffset x, y,
   * m_CropOrigSize w, h; null: none); null until loaded. */
  texInfo(ref: number | string): { w: number; h: number; crop: number[] | null } | null;
  /** The texture's kept pixels through the design-space affine `m`, tinted. */
  sprite(ref: number | string, m: Affine, colour: Rgba4): void;
  /** FM_Font::PrintAt 0xf23d50: `text` in font XID `font`, the pen at design (x, y) on the baseline. */
  print(text: string, font: number, x: number, y: number, colour: Rgba4): void;
  /** FM_Font::GetStringWidth and m_Height of font XID `font` (design units). */
  fontWidth(text: string, font: number): number;
  fontHeight(font: number): number;
};

/** `HU_TargetSquare_01`'s and the reticle's clip ("Go"), the arrows' loop. */
const GO = 'Go';
/** `TargetCursor::CursorType`. */
const CURSOR_TARGET = 0, CURSOR_DIRECTIONAL = 2;
/** `TargetCursor::CursorDirection`: k_Left, k_Right. */
const DIR_LEFT = 0, DIR_RIGHT = 1;
/** DAT_015a97c0: the select arrow's height over the worm (as the turn arrow's). */
const ARROW_UP = 175;
/** DAT_01593eac: the fuse timer shows up to 5 s; DAT_015ad7e8 / DAT_015b2434: its offset. */
const FUSE_MAX = 5, FUSE_DX = -25, FUSE_DY = 55;
/** DAT_015aa548 / DAT_015aa54c: the gadget icon's offset; DAT_015aa550 its scale. */
const ICON_DX = 50, ICON_DY = 82, ICON_SCALE = 0.6;
/** The clip time (seconds) of a clip started at frame `start`, shown at frame `tick`. */
const clipTime = (tick: number, start: number) => Math.max(0, tick - start) / 60;
const f = Math.fround;
/** TargetCursor::Initialize 0xc38650's textures; WormHUDActivity::Initialize 0xd08570's arrow. */
const RETICLE = 'HU_Reticle_01.png', STRIKE_ARROW = 'HU_AirStrikeArrow_01.png', SIDE_ARROW = 'HU_SideArrow_01.png';
/** DrawTracker's font: FM_FontManager::GetFont("WMD-SerifBold", 18), reslist font 1023. */
const F_SERIF18 = (9007 << 16) | 1023;
/** InterestEnum kInterest_Offscreen, kInterest_None. */
const INTEREST_OFFSCREEN = 0, INTEREST_NONE = 2;
/** DAT_015bf424 (the markers' ellipse, × the UI size), DAT_015aa4d8 (the distance's offset, × the
 * arrow's height), DAT_015aa45c (π/2 in float32). */
const ELLIPSE = f(0.45), TEXT_OFF = 1.5, HALF_PI = f(Math.PI / 2);

export class AimWidgets {
  /** The dirty frame each clip was last started from (`m_bDirtyAnimation` served). */
  private served = { reticle: 0, cursor: 0, select: 0 };
  /** The frame each clip actually started (a request met while hidden starts when shown). */
  private started = { reticle: 0, cursor: 0, select: 0 };
  /** WormHUDActivity's graphic state (m_uCount, m_Trackers). */
  private activity = new ActivityMarkers();

  /** The GUI meshes (crosshair, power bar, target square, select arrow) for frame `v`.
   * `local(team)`: the team is played on this machine (offline: every team). */
  draw3d(v: FrameView, r: Renderer, colour: (team: number) => Rgb, local: (team: number) => boolean): void {
    const w = v.widgets, tick = v.tick, team = colour(v.currentTeam);
    const hidden = hiddenFromLocal(w, local);
    // WormHUDAimReticle::GraphicUpdate 0xcf7780.
    const re = w.reticle;
    const reShow = !!re && re.active && !hidden;
    if (re && reShow && re.dirty !== this.served.reticle) {
      this.served.reticle = re.dirty;
      this.started.reticle = re.dirty;
    }
    const at = re ? r.worldToHud(re.pos[0], re.pos[1], re.pos[2]) : { x: 0, y: 0 };
    r.hudWidget('aimReticle', 'HU_Reticle_01', false, at.x + (re?.offset[0] ?? 0), at.y + (re?.offset[1] ?? 0), re?.rotation[2] ?? 0,
      re ? re.scale : [1, 1, 1], reShow, [{ clip: GO, time: clipTime(tick, this.started.reticle), weight: 1 }], team, null);
    // WormHUDPowerBar::GraphicUpdate 0xc91040: "Go" at time 0, weight 1 - power.
    const pb = w.powerBar;
    const pbShow = !!pb && pb.active && !hidden;
    const pa = pb ? r.worldToHud(pb.pos[0], pb.pos[1], pb.pos[2]) : { x: 0, y: 0 };
    const weight = pb ? 1 - pb.power : 1;
    const u = r.hudClipValue('powerBar', GO, 'BG_Trans_Emis_UVt|UvoffsetColorR', 0);
    r.hudWidget('powerBar', 'HU_PowerBar_01', true, pa.x, pa.y, pb?.rotation[2] ?? 0, [1, 1, 1], pbShow, [], [1, 1, 1], (u ?? 0.4) * weight);
    // TargetCursor::GraphicUpdate 0xc388f0, k_Target: the square mesh.
    const cu = w.cursor;
    const cuShow = !!cu && cu.active && !cu.hidden && cu.type === CURSOR_TARGET;
    if (cu && cuShow && cu.dirty !== this.served.cursor) {
      this.served.cursor = cu.dirty;
      this.started.cursor = cu.dirty;
    }
    const ca = cu ? r.worldToHud(cu.pos[0], cu.pos[1], cu.pos[2]) : { x: 0, y: 0 };
    r.hudWidget('targetCursor', 'HU_TargetSquare_01', false, ca.x + (cu?.offset[0] ?? 0), ca.y + (cu?.offset[1] ?? 0), 0, [1, 1, 1], cuShow,
      this.started.cursor ? [{ clip: GO, time: clipTime(tick, this.started.cursor), weight: 1 }] : [], cu?.valid ? team : [1, 1, 1], null);
    // WormHUDSelect::GraphicUpdate 0xafabf0: the looping "Go" restarted at each activation.
    const se = w.select;
    const seShow = !!se && se.active;
    if (se && seShow && se.once !== this.served.select) {
      this.served.select = se.once;
      this.started.select = se.once;
    }
    const sa = se ? r.worldToHud(se.pos[0], se.pos[1], se.pos[2]) : { x: 0, y: 0 };
    r.hudWidget('wormSelect', 'HU_WormSelect_01', false, sa.x, sa.y + ARROW_UP, 0, [1, 1, 1], seShow,
      [{ clip: GO, time: clipTime(tick, this.started.select), weight: 1 }], team, null);
  }

  /** The 2-D widgets (the directional target cursor, the fuse timer, the gadget icon, the
   * off-screen activity markers) on the HUD canvas. */
  draw2d(v: FrameView, r: Renderer, p: WidgetPainter, colour: (team: number) => Rgb): void {
    const w = v.widgets, team = colour(v.currentTeam);
    const cu = w.cursor;
    const ri = p.texInfo(RETICLE);
    if (cu && cu.active && !cu.hidden && cu.type === CURSOR_DIRECTIONAL && ri) {
      // TargetCursor::GraphicUpdate 0xc388f0, k_Directional: at WorldToHUD(m_vPosition), raised
      // by HU_Reticle_01.png's m_Height and for k_Right moved left by its m_Width, + m_vOffset;
      // the rect (0, 0, m_Width, m_Height) cropped by the reticle texture's crop data, textured
      // with HU_AirStrikeArrow_01.png (U swapped for k_Right); team colour, or (0.5, 0.5, 0.5,
      // 0.75) (DAT_01590164, DAT_015ad7f4) on an invalid position.
      const at = r.worldToHud(cu.pos[0], cu.pos[1], cu.pos[2]);
      let x = f(at.x), y = f(at.y);
      if (cu.direction === DIR_LEFT) y = f(ri.h + y);
      else if (cu.direction === DIR_RIGHT) [x, y] = [f(x - ri.w), f(ri.h + y)];
      x = f(x + cu.offset[0]);
      y = f(y + cu.offset[1]);
      const col: Rgba4 = cu.valid ? [team[0], team[1], team[2], 1] : [0.5, 0.5, 0.5, 0.75];
      xprimQuad(p, STRIKE_ARROW, RETICLE, [0, 0, ri.w, ri.h], 0, x, y, cu.direction === DIR_RIGHT, col);
    }
    const ft = w.fuseTimer;
    if (ft && ft.active && ft.time >= 0 && ft.time <= FUSE_MAX && ft.time !== -1) {
      const text = String(Math.trunc(ft.time) + 1);
      const at = r.worldToHud(ft.pos[0], ft.pos[1], ft.pos[2]);
      box(p, text, null, at.x + FUSE_DX, at.y + FUSE_DY, 1, [team[0], team[1], team[2], 1]);
    }
    const gi = w.gadgetIcon;
    if (gi && gi.active && gi.equip !== 0xffffffff) {
      const icon = p.equipIcon(gi.equip);
      const at = r.worldToHud(gi.pos[0], gi.pos[1], gi.pos[2]);
      if (icon !== null) box(p, null, icon, at.x + ICON_DX, at.y + ICON_DY, ICON_SCALE, [1, 1, 1, 1]);
    }
    // WormHUDActivity::GraphicUpdate 0xd08d10: nothing while inactive (the round results, which
    // also stop it, draw no labels).
    const ac = w.activity;
    if (ac && ac.active) {
      this.activity.update(ac, (x, y) => r.cameraMan.isOffscreen(x, y), (x, y, z) => r.worldToHud(x, y, z));
      this.activity.draw(p);
    }
  }
}

/** `WormMan::IsWormHiddingFromLocalPlayer(-1)` 0xc99ae0: in a challenge, campaign or bonus match
 * or a network game, a current worm whose label is hidden belongs to another player or the AI. */
export function hiddenFromLocal(w: Widgets, local: (team: number) => boolean): boolean {
  const h = w.hide;
  return h.capable && h.team >= 0 && !h.labelVisible && (!local(h.team) || h.ai);
}

/** The name panel box (HU_NamePanel_01/02/03: caps 8 wide, the box the content + 20 wide) centred
 * at (x, y) holding `text` or the icon `icon`, scaled by `scale`. */
function box(p: WidgetPainter, text: string | null, icon: number | string | null, x: number, y: number, scale: number, colour: [number, number, number, number]): void {
  const h = (p.size('HU_NamePanel_02.png')?.h ?? 32) * scale;
  const iconSz = icon !== null ? p.size(icon) : null;
  const content = text !== null ? p.textWidth(text) : (iconSz?.w ?? 42) * scale;
  const bw = content + 20 * scale, cap = 8 * scale;
  const tl = p.toCanvas(x - bw / 2, y + h / 2), br = p.toCanvas(x + bw / 2, y - h / 2);
  const rect: [number, number, number, number] = [tl.x, tl.y, br.x - tl.x, br.y - tl.y];
  const c = cap * p.s, white: [number, number, number, number] = [1, 1, 1, 1];
  p.texture('HU_NamePanel_01.png', [rect[0] - c, rect[1], c, rect[3]], white);
  p.texture('HU_NamePanel_02.png', rect, white);
  p.texture('HU_NamePanel_03.png', [rect[0] + rect[2], rect[1], c, rect[3]], white);
  if (text !== null) p.text(text, rect, colour);
  else if (icon !== null && iconSz) {
    const iw = iconSz.w * scale * p.s, ih = iconSz.h * scale * p.s;
    p.texture(icon, [rect[0] + (rect[2] - iw) / 2, rect[1] + (rect[3] - ih) / 2, iw, ih], white);
  }
}

/** An `XPRIM_QUAD` sub with the matrix flag 4: the rect (l, t, w, h; corners (l, t), (l + w, t),
 * (l, t - h), y up) after `cRM_Texture::UpdatePrimSubForCrop` 0xfcc770 of `cropRef`'s crop data
 * (w, h × new / original size; the offset (cx, cy) × the scaled size / new size, truncated, added
 * as (x, -y)), rotated by `rot` (XMatrix4::SetRotation: counter-clockwise) and moved to (x, y);
 * the corners truncated (`__Platform_BuildQuads` 0x113f610). `ref`'s kept sprite covers it, U
 * mirrored when `flipU`. */
function xprimQuad(p: WidgetPainter, ref: string, cropRef: string, rect: Rgba4, rot: number, x: number, y: number, flipU: boolean, colour: Rgba4): void {
  const ci = p.texInfo(cropRef), ti = p.texInfo(ref);
  if (!ci || !ti) return;
  let [l, t, w, h] = rect;
  let cx = 0, cy = 0;
  const cr = ci.crop;
  if (cr && cr[2] !== 0 && cr[3] !== 0) {
    w = f(f(ci.w / cr[2]) * w);
    h = f(f(ci.h / cr[3]) * h);
    cx = Math.trunc(f(f(f(f(ci.w * 0.5) + cr[0]) - f(0.5 * ci.w)) * f(w / ci.w)));
    cy = Math.trunc(f(f(f(f(ci.h * 0.5) + cr[1]) - f(0.5 * ci.h)) * f(h / ci.h)));
  }
  const c = f(Math.cos(rot)), s = f(Math.sin(rot));
  const ox = c * cx + s * cy, oy = s * cx - c * cy;
  const at = (lx: number, ly: number): [number, number] => [Math.trunc(lx * c - ly * s + x + ox), Math.trunc(lx * s + ly * c + y + oy)];
  const tl = at(l, t), tr = at(l + w, t), bl = at(l, t - h);
  const ux: [number, number] = [(tr[0] - tl[0]) / ti.w, (tr[1] - tl[1]) / ti.w];
  const vy: [number, number] = [(bl[0] - tl[0]) / ti.h, (bl[1] - tl[1]) / ti.h];
  const o = flipU ? tr : tl, k = flipU ? -1 : 1;
  p.sprite(ref, [k * ux[0], k * ux[1], vy[0], vy[1], o[0], o[1]], colour);
}

/** `WormHUDActivity::Tracker` (WormHUDActivity.h, 40 bytes). */
type Tracker = { index: number; sub: number; x: number; y: number; rot: number; dist: string; active: boolean };

/** WormHUDActivity's `GraphicUpdate` 0xd08d10 and `DrawTracker` 0xd08800 (the graphic state:
 * m_uCount, m_Trackers[8]; the rest comes with the frame, `Widgets.activity`). */
class ActivityMarkers {
  private count = 0;
  private trackers: Tracker[] = Array.from({ length: 8 }, () => ({ index: 0xffffffff, sub: 0, x: 0, y: 0, rot: 0, dist: '', active: false }));

  /** GraphicUpdate past its gate (active, no round results): newest objects first, an object off
   * screen (`CameraMan::IsOffscreen`) is tracked when outside the camera bounds (left, right or
   * above) with an interest other than kInterest_None, or inside them with kInterest_Offscreen,
   * and not ignored; trackers no longer found or back inside the HUD rect leave (the last moves
   * into the slot); the others sit on the 0.45-ellipse towards WorldToHUD((pos, 1)). */
  update(a: { bl: [number, number]; tr: [number, number]; objects: ActivityObject[] }, offscreen: (x: number, y: number) => boolean,
    toHud: (x: number, y: number, z: number) => { x: number; y: number }): void {
    const t = this.trackers, found = Array<boolean>(8).fill(false);
    for (let i = a.objects.length - 1; i >= 0 && this.count < 8; i--) {
      const o = a.objects[i];
      if (!offscreen(o.x, o.y)) continue;
      const outside = o.x < a.bl[0] || a.tr[0] < o.x || a.tr[1] < o.y;
      if (!(outside ? o.interest !== INTEREST_NONE : o.interest === INTEREST_OFFSCREEN) || o.ignored) continue;
      let j = this.count - 1;
      while (j >= 0 && !(t[j].index === o.index && t[j].sub === o.sub)) j--;
      if (j >= 0) found[j] = true;
      else {
        Object.assign(t[this.count], { index: o.index, sub: o.sub });
        found[this.count++] = true;
      }
    }
    for (let k = this.count; k < 8; k++) t[k].active = false;
    const hw = f(0.5 * DESIGN_W), hh = f(0.5 * DESIGN_H), rx = f(ELLIPSE * DESIGN_W), ry = f(ELLIPSE * DESIGN_H);
    for (let i = this.count - 1; i >= 0; i--) {
      const tr = t[i];
      const o = found[i] ? a.objects.find(x => x.index === tr.index && x.sub === tr.sub) : undefined;
      const hud = o ? toHud(o.x, o.y, 1) : null;
      if (hud && (hud.x < -hw || hw < hud.x || hh < hud.y || hud.y < -hh)) {
        const ang = f(Math.atan2(f(hud.x / rx), f(hud.y / ry)));
        const px = f(rx * f(Math.sin(ang))), py = f(ry * f(Math.cos(ang)));
        const dx = f(hud.x - px), dy = f(hud.y - py);
        Object.assign(tr, { active: true, x: px, y: py, rot: -ang, dist: String(Math.trunc(f(Math.sqrt(f(f(dy * dy) + f(dx * dx)))))) });
        continue;
      }
      tr.active = false;
      const last = t[--this.count];
      if (i !== this.count) Object.assign(tr, { index: last.index, sub: last.sub });
      if (found[i]) Object.assign(tr, { x: last.x, y: last.y, rot: last.rot, dist: last.dist, active: last.active });
    }
  }

  /** DrawTracker for every slot: the arrow's quad (0, H/2, W, H) cropped, rotated by rot - π/2
   * about the tracker and moved there; the distance in WMD-SerifBold 18, white over black 1 right
   * and 1 down, centred 1.5 × H inwards (pen at that point - (width, m_Height) / 2). */
  draw(p: WidgetPainter): void {
    const ai = p.texInfo(SIDE_ARROW);
    if (!ai) return;
    for (const tr of this.trackers) {
      if (!tr.active) continue;
      xprimQuad(p, SIDE_ARROW, SIDE_ARROW, [0, f(0.5 * ai.h), ai.w, ai.h], f(tr.rot - HALF_PI), tr.x, tr.y, false, [1, 1, 1, 1]);
      const ax = f(f(f(f(Math.sin(tr.rot)) * ai.h) * TEXT_OFF) + tr.x), ay = f(tr.y - f(f(f(Math.cos(tr.rot)) * ai.h) * TEXT_OFF));
      const x = f(ax - f(p.fontWidth(tr.dist, F_SERIF18) * 0.5)), y = f(ay - f(0.5 * p.fontHeight(F_SERIF18)));
      p.print(tr.dist, F_SERIF18, x + 1, y - 1, [0, 0, 0, 1]);
      p.print(tr.dist, F_SERIF18, x, y, [1, 1, 1, 1]);
    }
  }
}
